/**
 * 画面下部 sticky の確定バー (確定ボタン)。
 * index.tsx から presentational 部分を切り出した純表示コンポーネント。
 * 表示可否 (完了ケースで非表示) の判定は呼び出し側に残す。
 *
 * ページ主操作 (需要想定を確定) のため `PrimaryButton` を使う。
 * 旧実装は素の `Button` + `colorPalette="blue"` で塗りボタンを表現していたが、
 * ecloud v3 の `Button` 既定 variant は outline/ghost 系に倒れ「青文字リンク」に
 * 見える描画になっていたため、design (btn-primary = 塗り) 準拠で修正した。
 *
 * 確定ボタンは design L813 付近準拠で常に右端固定する。hint は `flex={1}` の
 * Box に常設し (= 有無に関わらず flex item を 2 つに保つ)、`justifyContent`
 * を指定しない (flex 既定 = flex-start) ことでボタンを末尾に固定する。以前は
 * `justifyContent="space-between"` + hint 条件付き描画だったため、hint が
 * 無い時は flex item が 1 つだけになり `space-between` が flex-start 相当に
 * fallback してボタンが左端へ移動する不具合があった (hint の出現/消滅でボタンが
 * 左右に動く)。
 */
import { useTranslation } from 'react-i18next'

import {
  Box,
  Flex,
  PrimaryButton,
  Text,
  Tooltip,
} from '@enechain/ecloud-designsystem'

import { ActionReasonText } from '~/components/common/ActionReasonText'

type Props = {
  /** 確定ボタンを無効化するか (未保存の小売あり / 確定中 / サブステップ未到達 等) */
  disabled: boolean
  /**
   * 無効時の押せない理由 (AC-DFC-004b、サブステップ未到達の理由)。 ボタンの左に
   * 文で出し、 hover の tooltip は補助として残す (spec「ツールチップは補助」)。
   * 未指定時はどちらも出さない (= 従来どおり disabled のみ、hint テキストで
   * 理由を示す既存 3 状態はこの prop の対象外)。
   */
  disabledReason?: string
  /** 無効時に理由を示す任意のヒント文言 (例: 未保存の小売がある) */
  hint?: string
  /** 確定 RPC 実行中か */
  isLoading: boolean
  /** 確定ボタン押下 (確定ダイアログを開く) */
  onClick: () => void
}

export const ConfirmBar = ({
  disabled,
  disabledReason,
  hint,
  isLoading,
  onClick,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const confirmButton = (
    <PrimaryButton disabled={disabled} loading={isLoading} onClick={onClick}>
      {t('page:demandForecastDaily.confirm.button')}
    </PrimaryButton>
  )
  return (
    <Box
      position="sticky"
      zIndex={10}
      // バーの下に余白を残さない (デザイナー指摘 2026-08-27「確定バーは下の余白不要」)。
      // 余白の正体は DS `SideMenuLayoutContentLayout` の外周 padding 16px。
      // css の `margin: ... -16px` で相殺しているつもりだったが、 `bottom={0}` の
      // sticky は containing block の content box にクランプするため負の margin-bottom を
      // 打ち消してしまう (実測: sticky+bottom:0 は margin-bottom の有無に関わらず 16px 残る)。
      // bottom を -16px にすると固着位置ごと下げられて 0px になる。
      // 8080 の `.dft-confirm-bar` は同じ理由で position:fixed へ移行済み
      // (forecast-today.html:45-49)。
      bottom="-16px"
      marginTop={4}
      background="Surface.primary"
      borderColor="Border.lowEmphasis"
      borderTopWidth="1px"
      boxShadow="md"
      css={{
        margin: '16px -16px -16px',
        padding: '12px 16px',
      }}
    >
      <Flex alignItems="center" flexWrap="wrap" gap={3}>
        {/* hint は常設の flex={1} Box に入れる (= 有無に関わらず flex item を 2 つ
            保ち、ボタンを常に末尾固定する。§JSDoc 参照) */}
        <Box flex={1} minWidth={0}>
          {hint !== undefined && (
            <Text color="Text.midEmphasis" fontSize="md">
              {hint}
            </Text>
          )}
        </Box>
        {/* 押せない理由はボタンの横に文で出す (8080 `#dft-confirm-note`)。 */}
        {disabledReason !== undefined && (
          <ActionReasonText>{disabledReason}</ActionReasonText>
        )}
        {/* disabledReason 指定時のみ Tooltip を付ける (hint テキストの 3 状態は
            この分岐の対象外)。 */}
        {disabledReason !== undefined ? (
          <Tooltip content={disabledReason} placement="top" showArrow>
            <span>{confirmButton}</span>
          </Tooltip>
        ) : (
          confirmButton
        )}
      </Flex>
    </Box>
  )
}
